import Vue from 'vue'
import { LicenceReportService } from '@/common/api'
import { rerankObjectArray, createAddToFn, createRemoveFromFn, createSetFn } from '@/common/utils/'
import deepFreeze from '@/common/utils/deepFreeze'
import { now } from '@/common/filters'

import { cloneDeep, omit, merge } from 'lodash'

import {
  LOAD_LICENCEREPORT,
  SAVE_LICENCEREPORT,
  TRASH_LICENCEREPORT,
  UNTRASH_LICENCEREPORT,
  DELETE_LICENCEREPORT
} from './actions.type'

import {
  ADD_LICENCEREPORT_ENTRY,
  REMOVE_LICENCEREPORT_ENTRY,
  SET_LICENCEREPORT_ENTRY,
  ADD_LICENCEREPORT_EXCHANGE_RATE,
  REMOVE_LICENCEREPORT_EXCHANGE_RATE,
  SET_LICENCEREPORT_EXCHANGE_RATE,
  ADD_LICENCEREPORT_STATUS,
  REMOVE_LICENCEREPORT_STATUS,
  SET_LICENCEREPORT_STATUS,
  SET_LICENCEREPORT_DIRTY,
  SET_LICENCEREPORT,
  RESET_STATE
} from './mutations.type'

export const STATUS_DONE = 'done'
export const STATUS_APPROVAL_PORTFOLIO_MANAGER = 'approval portfolio manager'
export const STATUS_APPROVAL_PORTFOLIO_MANAGEMENT = 'approval portfolio management'
export const STATUS_ALL_UPLOADED = 'all documents uploaded'
export const STATUS_FINALIZED = 'finalized'
export const STATUS_INVOICE_CREATED = 'invoice created'
export const STATUS_ZS_QU2_FORM_AQUIRED = 'ZS QU2 form aquired'
export const STATUS_PAYMENT_RECEIVED = 'payment received'
export const STATUS_REPORT_SENT = 'report sent'
export const STATUS_INVOICE_RECEIVED = 'invoice received/assigned'
export const STATUS_PAYMENT_EXECUTED = 'payment executed'

export const defaultExchangeRate = deepFreeze({
  id: null,
  rank: 1,
  currency_base: 'EUR',
  currency_to: 'USD',
  rate: 0.0,
  comment: '',
  updated_at: 0
})

export const defaultEntry = deepFreeze({
  contract_subject_id: null,
  id: null,
  rank: 1,
  crop_id: 0,
  generation: '',
  country: '',
  price: 0.0,
  currency: 'EUR',
  amount: 0.0,
  unit: 'kg',
  free_amount: 0.0,
  trashed_amount: 0.0,
  client: ''
})

export const defaultStatus = deepFreeze({
  licencereport_id: null,
  id: null,
  name: '',
  date: null,
  comment: ''
})

export const defaultMeta = deepFreeze({
  dirty: false,
  originalContacts: {},
  created_at: null,
  updated_at: null,
  deleted_at: null,
  modificator: null,
  creator: null,
  data_owned_by: {},
  data_shared_with: []
})

// FIXME Patrik: it is probably wise to refactor state into {current,original,metadata,...}
const initialState = deepFreeze({
  id: null,

  correction_id: null,
  preliminary_id: null,
  replaces_id: null,
  corrects_id: null,

  type: 'report',
  derived: false,
  derivedEntries: [],

  title: '',
  comment: '',
  documents: [],
  licensor: { id: 0, name: '' },
  licencee: { id: 0, name: '' },
  year: 0,
  season: 'none',
  culture: '',
  entries: [],

  exchangeRates: [],
  statuses: [],
  portfolioManager: { id: 0 },
  _meta: cloneDeep(defaultMeta)
})

export const state = cloneDeep(initialState)

export const actions = {
  async [LOAD_LICENCEREPORT] ({ commit }, id) {
    Vue.$log.debug('LOAD_LICENCEREPORT', id)
    const { data } = await LicenceReportService.get(id)
    const licencereport = {
      ...cloneDeep(initialState),
      ...data,
      _meta: merge(cloneDeep(initialState._meta), data._meta, { loaded_at: now() })
    }

    commit(SET_LICENCEREPORT, licencereport)

    return licencereport
  },
  async [SAVE_LICENCEREPORT] ({ commit }, payload) {
    Vue.$log.debug('SAVE_LICENCEREPORT', payload.id)
    const { data } = await LicenceReportService[payload.id ? 'post' : 'put'](omit(payload, ['_meta']))
    const licencereport = {
      ...cloneDeep(initialState),
      ...data,
      _meta: merge(cloneDeep(initialState._meta), data._meta, { loaded_at: now() })
    }

    commit(SET_LICENCEREPORT, licencereport)
    return licencereport
  },
  async [TRASH_LICENCEREPORT] ({ commit }, { id }) {
    Vue.$log.debug('TRASH_LICENCEREPORT')
    const { data } = await LicenceReportService.trash(id)
    Vue.$log.debug('trashed licencereport:', id)
    commit(SET_LICENCEREPORT, data)
    return data
  },
  async [UNTRASH_LICENCEREPORT] ({ commit }, { id }) {
    Vue.$log.debug('UNTRASH_LICENCEREPORT')
    const { data } = await LicenceReportService.untrash(id)
    Vue.$log.debug('untrashed licencereport:', id)
    commit(SET_LICENCEREPORT, data)
    return data
  },
  async [DELETE_LICENCEREPORT] ({ commit }, { id }) {
    Vue.$log.debug('DELETE_LICENCEREPORT')
    await LicenceReportService.delete(id)
    Vue.$log.debug('deleted licencereport:', id)
    commit(RESET_STATE)
  }
}

export const mutations = {
  [SET_LICENCEREPORT_DIRTY] (state) {
    state._meta.dirty = true
  },
  [ADD_LICENCEREPORT_EXCHANGE_RATE]: createAddToFn('exchangeRates', defaultExchangeRate),
  [REMOVE_LICENCEREPORT_EXCHANGE_RATE]: createRemoveFromFn('exchangeRates'),
  [SET_LICENCEREPORT_EXCHANGE_RATE]: createSetFn('exchangeRates'),
  [ADD_LICENCEREPORT_ENTRY]: createAddToFn('entries', defaultEntry),
  [REMOVE_LICENCEREPORT_ENTRY]: createRemoveFromFn('entries'),
  [SET_LICENCEREPORT_ENTRY]: createSetFn('entries'),
  [ADD_LICENCEREPORT_STATUS]: createAddToFn('statuses', defaultStatus),
  [REMOVE_LICENCEREPORT_STATUS]: createRemoveFromFn('statuses'),
  [SET_LICENCEREPORT_STATUS]: createSetFn('statuses'),
  [SET_LICENCEREPORT] (state, licencereport) {
    Vue.$log.debug('SET_LICENCEREPORT', licencereport)
    // since entries is an array of objects, recalc a incrementing id
    rerankObjectArray(licencereport.entries)
    for (const f in state) {
      Vue.set(state, f, licencereport[f])
    }
  },
  [RESET_STATE] () {
    Vue.$log.debug('RESET_STATE')
    for (const f in state) {
      Vue.set(state, f, cloneDeep(initialState[f]))
    }
  }
}

const getters = {
  id (state) {
    return state.id || null
  },
  pageTitle (state) {
    return String(state.title || '').trim()
  },
  licencereport (state) {
    return state
  },
  isApprovedNotSaved (report, { exists }) {
    return exists && report.statuses.some(({ id, name }) => name === STATUS_APPROVAL_PORTFOLIO_MANAGEMENT && !id)
  },
  isApproved (report, { exists }) {
    return exists && report.statuses.some(({ name }) => name === STATUS_APPROVAL_PORTFOLIO_MANAGEMENT)
  },
  isPreliminaryReport (report, { exists, isCorrection }) {
    return exists && !isCorrection && !Boolean(report.preliminary_id)
  },
  hasFinalReport (report, { isPreliminaryReport }) {
    return isPreliminaryReport && Boolean(report.replaces_id)
  },
  isFinalized (report) {
    return Boolean(report.statuses.find(({ name }) => name === STATUS_FINALIZED))
  },
  canCreateFinalReport (report, { exists, isFinalized, hasFinalReport, isCorrection, isApproved }) {
    return exists && isFinalized && !isCorrection && isApproved && !hasFinalReport && !Boolean(report.preliminary_id)
  },
  canCreateCorrection (report, { exists, hasFinalReport, isFinalized, isCorrection }) {
    return exists && isFinalized && !isCorrection && !hasFinalReport
  },
  isCorrection (report, { exists }) {
    return exists && report.type === 'correction'
  },
  licensorIsRwa (state) {
    return Number(state.licensor?.id) === 325 // FIXME: hardcoded db-id
  },
  licenceeIsRwa (state) {
    return Number(state.licencee?.id) === 325 // FIXME: hardcoded db-id
  },
  isDerived (report, { exists }) {
    return exists && Boolean(report.derived)
  },
  hasDerivedEntries (state) {
    return Boolean(state.derived) && Boolean(state.derivedEntries.length)
  },
  entries (state) {
    return state.entries || []
  },
  culture (state) {
    return state.culture || ''
  },
  portfolioManager (state) {
    return state.portfolioManager || { id: 0 }
  },
  missingExchangeRates ({ entries, exchangeRates }) {
    const inUse = new Set((entries ?? []).map(e => e.currency).filter(c => c && c !== 'EUR'))
    const asRate = new Set((exchangeRates ?? []).map(e => e.currency_to))

    return [...inUse.difference(asRate)].toSorted()
  },
  statuses (state) {
    return state.statuses || []
  },
  exists (state) {
    return Boolean(state.id)
  },
  isDirty (state, { isPristine }) {
    return !isPristine
  },
  isPristine (state, { exists }) {
    return !exists || !state._meta.dirty
  },
  isInValid (state) {
    return !state.licensor?.id || !state.licencee?.id || !state.year || !state.season
  },
  isValid (state, { isInValid }) {
    return !isInValid
  },
  dataOwner (state) {
    return state._meta.data_owned_by
  },
  metaData (state) {
    return state._meta
  }
}

const namespaced = true

export default {
  namespaced,
  state,
  actions,
  mutations,
  getters
}
